import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Zap } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { getCreditPacks, createCreditPackCheckout } from '../services/credits';

/**
 * "Comprar créditos" modal — lets a user top up image-generation credits
 * on demand via a one-time Stripe Checkout (no subscription change).
 * Packs are fetched from GET /api/credits/packs so prices stay backend-owned.
 *
 * `currentBalance` (the purchased_balance already known by Publications.jsx)
 * is stashed in sessionStorage right before redirecting to Stripe — the
 * webhook that credits the purchase can fire in well under a second, often
 * faster than the browser makes it back from Stripe's redirect, so
 * CreditsReturn.jsx can't reliably capture "before" by polling after it
 * arrives. sessionStorage survives the round-trip to Stripe's hosted
 * checkout and back since it's the same tab/origin.
 */
export default function BuyCreditsModal({ open, onOpenChange, currentBalance = 0 }) {
  const [packs, setPacks] = useState([]);
  const [loadingPacks, setLoadingPacks] = useState(false);
  const [buyingPack, setBuyingPack] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoadingPacks(true);
    getCreditPacks()
      .then(setPacks)
      .catch(() => toast.error('No se pudieron cargar los paquetes de créditos'))
      .finally(() => setLoadingPacks(false));
  }, [open]);

  const handleBuy = async (packSize) => {
    setBuyingPack(packSize);
    try {
      const { checkout_url } = await createCreditPackCheckout(packSize);
      if (checkout_url) {
        sessionStorage.setItem('credit_purchase_baseline', String(currentBalance));
        window.location.href = checkout_url;
        return;
      }
      toast.error('No se pudo iniciar el pago');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo iniciar el pago con Stripe');
    } finally {
      setBuyingPack(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-blue-500" />
            Comprar créditos de generación
          </DialogTitle>
          <DialogDescription>
            Pago único, no es una suscripción. Los créditos se suman a tu saldo y no expiran.
          </DialogDescription>
        </DialogHeader>

        {loadingPacks ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid gap-3">
            {packs.map((pack) => (
              <button
                key={pack.pack_size}
                onClick={() => handleBuy(pack.pack_size)}
                disabled={buyingPack !== null}
                data-testid={`buy-credits-pack-${pack.pack_size}`}
                className="flex items-center justify-between rounded-lg border border-border bg-card/60 px-4 py-3 text-left transition-colors hover:border-blue-500/50 hover:bg-blue-500/5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="font-medium text-foreground">{pack.pack_size} créditos</span>
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  {buyingPack === pack.pack_size && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  ${(pack.price_cents / 100).toFixed(2)} USD
                </span>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
